import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { WithCompareByIdFn } from 'core/mixins/with-compare-by-id-fn';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { CreateEntityConnectionService } from 'entity/helpers/create-entity-connection.service';
import { getOppositeEntityTypeFromConnection } from 'entity/helpers/get-connected-to-entity-type';
import { isNilOrEmpty } from 'helpers';

import {
  CreateEntityConnectionInput, EntityConnectionTypeDetailsFragment,
  EntityConnectionWithTagsFragment, EntityPublicInfoFragment, GetEntityConnectionGQL,
  GetEntityConnectionTypesGQL
} from 'generated/graphql';

/**
 * Format of data for the dialog
 */
export interface AddConnectionDialogData {
  /** Optional entity connection type */
  entityConnectionType?: EntityConnectionTypeDetailsFragment;
  campaignId: string;
  fromEntityId: string;
  fromEntityTypeId: string;
}

@Component({
  selector: 'app-add-connection-dialog',
  templateUrl: './add-connection.dialog.html',
  styleUrls: ['./add-connection.dialog.scss']
})
export class AddConnectionDialog extends WithDisposableSubject(
  WithCompareByIdFn(class { })
)
  implements OnInit {

  isSaving = false;
  entityConnectionOptions: EntityConnectionTypeDetailsFragment[];

  toEntityType: { nameSingular: string; id: string };

  selectedEntity: EntityPublicInfoFragment;
  entityConnectionTypeInput = new UntypedFormControl();
  noteInput = new UntypedFormControl();


  // Set after connection is created
  createdConnection: EntityConnectionWithTagsFragment;

  get saveDisabled() {
    return this.isSaving || !this.selectedEntity;
  }

  static open(dialog: MatDialog, config: MatDialogConfig<AddConnectionDialogData>) {
    return dialog.open(AddConnectionDialog, config);
  }

  constructor(
    private _createEntityConnectionSvc: CreateEntityConnectionService,
    private _cd: ChangeDetectorRef,
    private _getEntityConnectionTypes: GetEntityConnectionTypesGQL,
    private _getEntityConnectionGQL: GetEntityConnectionGQL,
    public dialogRef: MatDialogRef<AddConnectionDialog>,
    @Inject(MAT_DIALOG_DATA) public data: AddConnectionDialogData
  ) {
    super();

  }

  ngOnInit(): void {
    this._setupForm();
  }

  close() {
    this.dialogRef.close();
  }

  entitySelected(event: any) {
    this.selectedEntity = event;
  }

  excludeEntitiesFn(entityId: string) {
    return function(results: EntityPublicInfoFragment[]) {
      return results.filter(e => e.id !== entityId);
    };
  }

  onSubmitClick() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    this._createEntityConnection()
      .pipe(
        tap(() => {
          this.isSaving = false;
        }),
        switchMap(
          ({ data }) => this._getEntityConnectionGQL.watch({
            campaignId: data.createEntityConnection.entityConnection.campaignId,
            entityConnectionId: data.createEntityConnection.entityConnection.id
          }).valueChanges.pipe(
            takeUntil(this.disposer)
          )
        )
      )
      .subscribe({
        next: (next) => {
          this.createdConnection = next.data.getEntityConnection;
          this._cd.markForCheck();
        },
        error: (err) => {
          this.isSaving = false;
        }
      });
  }

  getOppositeEntityType(ect: EntityConnectionTypeDetailsFragment) {
    return getOppositeEntityTypeFromConnection(this.data.fromEntityTypeId, ect);
  }

  private _createEntityConnection() {
    const input: CreateEntityConnectionInput = {
      entityConnectionTypeId: this.entityConnectionTypeInput.value.id,
      campaignId: this.data.campaignId,
      fromEntityId: this.data.fromEntityId,
      toEntityId: this.selectedEntity?.id,
      note: this.noteInput.value
    };

    return this._createEntityConnectionSvc.createEntityConnection(input);
  }

  private _setupForm() {
    // if an entityConnectionType is provided, set the selected form value
    // and the toEntityType
    if (this.data.entityConnectionType) {
      this.toEntityType = getOppositeEntityTypeFromConnection(
        this.data.fromEntityTypeId, this.data.entityConnectionType
      );
      this.entityConnectionTypeInput.setValue(this.data.entityConnectionType);
    }

    // Watch for changes on connection Type input
    this.entityConnectionTypeInput.valueChanges
      .subscribe(value => {
        // Reset selected entity if changed entityType
        this.selectedEntity = null;
        // Set to entity type (avoids multiple fn calls on change detection)
        this.toEntityType = getOppositeEntityTypeFromConnection(
          this.data.fromEntityTypeId, value
        );
        this._cd.markForCheck();
      });

    this._getEntityConnectionTypes.watch({
      campaignId: this.data.campaignId,
      entityTypeId: this.data.fromEntityTypeId
    }, {
      fetchPolicy: 'cache-and-network'
    }).valueChanges
      .pipe(
        takeUntil(this.disposer),
        filter(res => !!res.data),
        map(({ data }) =>
          // Filter to only connection types where a valid entity type id is the current entity type
          data.getEntityConnectionTypes
            .filter(ect => [ect.entityType1.id, ect.entityType2.id].includes(this.data.fromEntityTypeId))
        )
      ).subscribe(
        (ects) => {
          this.entityConnectionOptions = ects;
          // Set to the first type if there is only one type
          if (isNilOrEmpty(this.entityConnectionTypeInput.value)) {
            if (ects.length === 1) {
              this.entityConnectionTypeInput.setValue(ects[0]);
            }
          }
          this._cd.markForCheck();
        }
      );
  }
}
